import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Modal, Table, Input, Select, Space, Image, Button, Typography, message, Badge } from "antd";
import { SearchOutlined, SyncOutlined, PlusCircleOutlined, ShoppingOutlined, DeleteOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { productAPIWithErrorHandling } from '../../../../api/product/product';
import { categoryAPIWithErrorHandling } from '../../../../api/product/category';
import ProductForm from '../../Product/components/ProductForm';
import { resolveImageUrl } from '../../../../utils/image';
import { neutral, brand, border } from '../../../../theme/tokens';
import StatusTag from "../../../../components/cms/StatusTag";

const { Text } = Typography;

const ProductImage = React.memo(function ProductImage({ src }) {
  if (!src) return null;
  return (
    <Image
      src={resolveImageUrl(src)}
      width={32}
      height={32}
      className="rounded object-cover"
      preview={false}
    />
  );
});

export default function ProductSelectorModal({
  open,
  onCancel,
  onSelect,
  selectedIds = [],
  initialItems = [],
  branchId = null,
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);

  // Selected lưu Map<id, item> — tách rời khỏi page hiện tại
  const [tempSelected, setTempSelected] = useState(new Map());
  const itemMapRef = useRef(new Map());

  const [viewMode, setViewMode] = useState('all'); // 'all' | 'selected'

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({ search: '', service_category_id: '' });
  const [searchInput, setSearchInput] = useState('');

  // Bootstrap itemMap với initialItems (cho Edit mode)
  useEffect(() => {
    initialItems.forEach((item) => {
      if (item?.id) itemMapRef.current.set(item.id, item);
    });
  }, [initialItems]);

  // Khi mở modal: sync selectedIds vào tempSelected
  useEffect(() => {
    if (open) {
      const map = new Map();
      selectedIds.forEach((id) => {
        const item = itemMapRef.current.get(id) || { id, name: `SP #${id}` };
        map.set(id, item);
      });
      setTempSelected(map);
      setViewMode('all');
      setSearchInput('');
      setFilters({ search: '', service_category_id: '' });
      fetchCategories();
      fetchData({ page: 1, search: '', service_category_id: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branchId]);

  const fetchCategories = async () => {
    try {
      const res = await categoryAPIWithErrorHandling.getList({ per_page: 100 });
      if (res.success) setCategories(res.data.data || []);
    } catch (e) {
      // silent
    }
  };

  const fetchData = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const res = await productAPIWithErrorHandling.getList({
          page: params.page ?? pagination.current,
          per_page: params.pageSize ?? pagination.pageSize,
          mcoupon_branch_id: branchId,
          search: params.search ?? filters.search,
          service_category_id: params.service_category_id ?? filters.service_category_id,
        });
        if (res.success) {
          const list = res.data.data || [];
          setData(list);
          setPagination((prev) => ({
            ...prev,
            current: res.data.current_page || params.page || 1,
            total: res.data.total || list.length,
          }));
          list.forEach((item) => itemMapRef.current.set(item.id, item));
        }
      } catch (error) {
        message.error('Lỗi tải danh sách sản phẩm');
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [branchId]
  );

  // Debounce search
  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        setFilters((prev) => ({ ...prev, search: val }));
        fetchData({ page: 1, search: val });
      }, 400),
    [fetchData]
  );

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchInput(v);
    debouncedSearch(v);
  };

  const handleCategoryChange = (val) => {
    const newFilters = { ...filters, service_category_id: val || '' };
    setFilters(newFilters);
    fetchData({ page: 1, service_category_id: val || '' });
  };

  const handleTableChange = (nav) => {
    fetchData({ page: nav.current, pageSize: nav.pageSize });
  };

  const handleRowToggle = useCallback((record) => {
    setTempSelected((prev) => {
      const next = new Map(prev);
      if (next.has(record.id)) next.delete(record.id);
      else next.set(record.id, record);
      return next;
    });
  }, []);

  const handleClearAll = () => setTempSelected(new Map());

  const handleOk = () => {
    const ids = Array.from(tempSelected.keys());
    const rows = Array.from(tempSelected.values());
    rows.forEach((r) => itemMapRef.current.set(r.id, r));
    onSelect(rows, ids);
    onCancel();
  };

  const selectedList = useMemo(() => Array.from(tempSelected.values()), [tempSelected]);
  const displayData = viewMode === 'selected' ? selectedList : data;

  const columns = useMemo(
    () => [
      {
        title: 'Sản phẩm',
        key: 'product',
        render: (_, record) => (
          <Space>
            <ProductImage src={record.image} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text strong style={{ fontSize: 13 }}>{record.name}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{record.code}</Text>
            </div>
          </Space>
        ),
      },
      {
        title: 'Loại',
        dataIndex: 'typeName',
        key: 'typeName',
        width: 100,
        render: (type, record) => (
          <StatusTag style={{ fontSize: 11 }}>
            {type || 'Sản phẩm'}
          </StatusTag>
        ),
      },
      {
        title: 'Giá',
        dataIndex: 'price',
        key: 'price',
        width: 100,
        render: (price) => <Text style={{ fontSize: 12 }}>{price?.toLocaleString() || 0} đ</Text>,
      },
      {
        title: 'Tồn kho',
        dataIndex: 'remain_stock',
        key: 'remain_stock',
        width: 90,
        align: 'center',
        render: (stock) => (
          <StatusTag tone={stock <= 0 ? "danger" : "success"} style={{ fontSize: 11 }}>
            {stock}
          </StatusTag>
        ),
      },
      {
        title: 'Danh mục',
        dataIndex: 'category_name',
        key: 'category_name',
        width: 120,
        render: (name) => <StatusTag style={{ fontSize: 11 }}>{name || "---"}</StatusTag>,
      },
    ],
    []
  );

  const rowSelection = {
    selectedRowKeys: Array.from(tempSelected.keys()),
    onSelect: (record, selected) => {
      setTempSelected((prev) => {
        const next = new Map(prev);
        if (selected) next.set(record.id, record);
        else next.delete(record.id);
        return next;
      });
    },
    onSelectAll: (selected, _rows, changeRows) => {
      setTempSelected((prev) => {
        const next = new Map(prev);
        changeRows.forEach((r) => {
          if (selected) next.set(r.id, r);
          else next.delete(r.id);
        });
        return next;
      });
    },
  };

  return (
    <Modal
      title={
        <div style={{ fontSize: 16, fontWeight: 600, color: neutral[900], display: 'flex', alignItems: 'center', gap: 10 }}>
          <ShoppingOutlined /> Chọn sản phẩm quà tặng
          <Badge count={tempSelected.size} showZero style={{ backgroundColor: brand.accent, marginLeft: 8 }} />
        </div>
      }
      open={open}
      onCancel={onCancel}
      width={950}
      styles={{
        body: { padding: 20 },
        header: { borderBottom: border.hairline, paddingBottom: 14 },
      }}
      footer={[
        <Button key="clear" onClick={handleClearAll} disabled={tempSelected.size === 0} icon={<DeleteOutlined />}>
          Bỏ chọn tất cả
        </Button>,
        <Button key="cancel" onClick={onCancel}>
          Hủy
        </Button>,
        <Button key="ok" type="primary" onClick={handleOk}>
          Xác nhận ({tempSelected.size})
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Input
            placeholder="Tìm theo tên hoặc mã sản phẩm..."
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={handleSearchChange}
            allowClear
            style={{ flex: '1 1 260px', minWidth: 200 }}
            disabled={viewMode === 'selected'}
          />
          <Select
            placeholder="Lọc theo danh mục"
            style={{ width: 180 }}
            allowClear
            onChange={handleCategoryChange}
            value={filters.service_category_id || undefined}
            options={categories.map((c) => ({ label: c.name, value: c.id }))}
            disabled={viewMode === 'selected'}
          />
          <Button.Group>
            <Button
              type={viewMode === 'all' ? 'primary' : 'default'}
              onClick={() => setViewMode('all')}
            >
              Tất cả
            </Button>
            <Button
              type={viewMode === 'selected' ? 'primary' : 'default'}
              onClick={() => setViewMode('selected')}
            >
              Đã chọn ({tempSelected.size})
            </Button>
          </Button.Group>
          <Button
            type="dashed"
            icon={<PlusCircleOutlined />}
            onClick={() => setIsQuickAddOpen(true)}
          >
            Thêm mới
          </Button>
          <Button icon={<SyncOutlined />} onClick={() => fetchData()} title="Làm mới" />
        </div>

        <Table
          scroll={{ x: "max-content" }}
          rowSelection={viewMode === 'all' ? rowSelection : undefined}
          columns={columns}
          dataSource={displayData}
          loading={viewMode === 'all' && loading}
          rowKey="id"
          pagination={
            viewMode === 'all'
              ? {
                  ...pagination,
                  size: 'small',
                  showTotal: (total) => `Tổng ${total} sản phẩm`,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                }
              : false
          }
          onChange={handleTableChange}
          size="small"
          bordered
          onRow={(record) => ({
            onClick: () => {
              if (viewMode === 'all') handleRowToggle(record);
              else handleRowToggle(record);
            },
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: viewMode === 'selected' ? 'Chưa chọn sản phẩm nào' : 'Không có sản phẩm',
          }}
        />
      </Space>

      <ProductForm
        open={isQuickAddOpen}
        onCancel={() => setIsQuickAddOpen(false)}
        branchId={branchId}
        categories={categories}
        onSuccess={(newProduct) => {
          setIsQuickAddOpen(false);
          fetchData({ page: 1 });
          if (newProduct && newProduct.id) {
            setTempSelected((prev) => {
              const next = new Map(prev);
              if (!next.has(newProduct.id)) next.set(newProduct.id, newProduct);
              return next;
            });
            itemMapRef.current.set(newProduct.id, newProduct);
            message.success(`Đã tự động chọn: ${newProduct.name}`);
          }
        }}
      />
    </Modal>
  );
}
